import { useEffect } from "react";

import {
  FeatureFlag,
  useFeatureFlag,
} from "@vega-api/extended/auth/useFeatureFlag";
import { type CollaboratorDetails } from "@vega-api/generated/types/collaboratorDetails";
import { Avatar } from "@vega-core/components/Avatar/Avatar";
import { CloseButton } from "@vega-core/components/Buttons/CloseButton/CloseButton";
import { SidebarModal } from "@vega-core/components/Modals/SidebarModal/SidebarModal";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { formatShortSlashDate } from "@vega-core/utils/formatDate";
import { formatFullName } from "@vega-core/utils/formatString";

import { CollaboratorsSideDrawerHeader } from "./CollaboratorsSideDrawerHeader";
import { CollaboratorsSideDrawerRoles } from "./CollaboratorsSideDrawerRoles";
import { CollaboratorsSideDrawerV2Content } from "./CollaboratorsSideDrawerV2Content";

type CollaboratorsSideDrawerProps = {
  isSideDrawerOpen: boolean;
  selectedCollaborator: CollaboratorDetails | null;
  setSelectedCollaborator: (collaborator: CollaboratorDetails | null) => void;
  setIsSideDrawerOpen: (isOpen: boolean) => void;
};

export const CollaboratorsSideDrawer = ({
  isSideDrawerOpen,
  selectedCollaborator,
  setSelectedCollaborator,
  setIsSideDrawerOpen,
}: CollaboratorsSideDrawerProps) => {
  const { featureFlag } = useFeatureFlag();
  const isCollaboratorsV2 = featureFlag[FeatureFlag.GP_COLLABORATORS_V2];

  const collaboratorName = formatFullName({
    firstName: selectedCollaborator?.firstName || "",
    middleName: selectedCollaborator?.middleName,
    lastName: selectedCollaborator?.lastName || "",
  });

  useEffect(() => {
    if (!isSideDrawerOpen) {
      setSelectedCollaborator(null);
    }
  }, [isSideDrawerOpen, setSelectedCollaborator]);

  return (
    <SidebarModal
      customWidthClassnames={cnTwMerge(
        "max-w-3xl",
        isCollaboratorsV2 && "max-w-2xl",
      )}
      dataTestId="collaborators"
      hideCloseButton
      isOpen={isSideDrawerOpen}
      noPadding
      setIsOpen={setIsSideDrawerOpen}
    >
      {isCollaboratorsV2 ? (
        <>
          <div className="flex items-center justify-between w-full bg-text/secondary/10 px-6 py-4">
            <div className="flex items-center gap-3">
              <Avatar
                className="w-10 h-10"
                fontClassName="heading-4"
                initials={collaboratorName ? collaboratorName[0] : "-"}
              />
              <div className="flex flex-col">
                <div className="body-base-medium text-text/primary capitalize">
                  {collaboratorName}
                </div>
                <div className="body-small-regular text-text/secondary">
                  {selectedCollaborator?.email.toLocaleLowerCase() || "-"}
                </div>
                <div className="body-small-regular text-text/secondary">
                  {m.common_lastActive()}{" "}
                  {selectedCollaborator?.lastActivity
                    ? formatShortSlashDate(selectedCollaborator.lastActivity)
                    : "-"}
                </div>
              </div>
            </div>
            <CloseButton onClick={() => setIsSideDrawerOpen(false)} />
          </div>
          <CollaboratorsSideDrawerV2Content
            isSideDrawerOpen={isSideDrawerOpen}
            selectedCollaborator={selectedCollaborator}
          />
        </>
      ) : (
        <>
          <div className="relative h-[4.5rem] w-full">
            <div className="absolute top-4 right-4">
              <CloseButton onClick={() => setIsSideDrawerOpen(false)} />
            </div>
          </div>
          <div className="flex flex-col w-full h-full px-6 pb-8 gap-6">
            <CollaboratorsSideDrawerHeader
              collaboratorName={collaboratorName}
              selectedCollaborator={selectedCollaborator}
            />
            {selectedCollaborator ? (
              <CollaboratorsSideDrawerRoles
                selectedCollaborator={selectedCollaborator}
              />
            ) : null}
          </div>
        </>
      )}
    </SidebarModal>
  );
};
